import { apiClient } from './apiClient';

export type ContractPaymentStatus =
  | 'NOT_FUNDED'
  | 'PAYMENT_PENDING'
  | 'FULLY_FUNDED'
  | 'EXPIRED'
  | 'FULLY_RELEASED'
  | 'PARTIALLY_REFUNDED';

export type MilestonePaymentStatus = 'UNFUNDED' | 'FUNDED' | 'RELEASED' | 'REFUNDED';

export interface MerchantStatus {
  registered: boolean;
  alipayAccount?: string;
  holderName?: string;
  subMerchantId?: string;
  registeredAt?: number;
}

export interface PaymentInfo {
  paymentStatus: ContractPaymentStatus;
  totalReleased: number;
  totalFee: number;
  totalRefunded: number;
  paidAt?: number;
  paymentDeadlineAt?: number;
  tradeNo?: string;
  platformFeeRate: number;
  milestones: Array<{
    milestoneId: string;
    title: string;
    paymentAmount: number;
    paymentStatus?: MilestonePaymentStatus;
    releasedAmount?: number;
    platformFeeAmount?: number;
    releasedAt?: number;
    refundedAt?: number;
    status: string;
  }>;
}

export interface CreatePaymentResult {
  tradeNo: string;
  qrCodeUrl: string;
  payUrl: string;
  expiresAt: number;
}

export const paymentService = {
  setupMerchant: async (alipayAccount: string, holderName: string, contactPhone?: string) => {
    return apiClient.post<{ subMerchantId: string }>(
      '/api/v1/payment/setup-merchant',
      { alipayAccount, holderName, contactPhone },
    );
  },

  getMerchantStatus: async () => {
    return apiClient.get<MerchantStatus>('/api/v1/payment/merchant-status');
  },

  replaceAccount: async (alipayAccount: string, holderName: string) => {
    return apiClient.post<{ success: boolean }>(
      '/api/v1/payment/replace-account',
      { alipayAccount, holderName },
    );
  },

  deregisterMerchant: async () => {
    return apiClient.post<{ success: boolean; blockingContracts?: string[] }>(
      '/api/v1/payment/deregister-merchant',
    );
  },

  createPaymentOrder: async (contractId: string) => {
    return apiClient.post<CreatePaymentResult>(
      `/api/v1/payment/contracts/${contractId}/pay`,
    );
  },

  getPaymentInfo: async (contractId: string) => {
    return apiClient.get<PaymentInfo>(`/api/v1/payment/contracts/${contractId}/info`);
  },

  // DEV-only — pretend Alipay confirmed the payment so we can test post-payment flow
  simulatePayment: async (contractId: string) => {
    return apiClient.post<{ success: boolean }>(
      `/api/v1/payment/contracts/${contractId}/simulate-payment`,
    );
  },
};

export default paymentService;
